import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { QRProps } from "@/contexts/QREditorContext/types";
import { usePreviewData } from "@/features/previews/shared/hooks/usePreviewData";
import { useToast } from "@/hooks/ui/useToast";
import { isFileAblob } from "@/utils/qrEditorHelper";
import { InvalidVideoFormatToast, InvalidYTProviderToast } from "@/utils/uploadHelper";
import { NAMESPACES } from "@/internals/constants/namespaces";

import { UseYoutubeDataResult } from "../types";
import { logError } from "@/services/DatadogService";

const YOUTUBE_OEMBED_ENDPOINT = "https://www.youtube.com/oembed?url=";
const EMPTY_VIDEO_IFRAME =
  '<iframe style="width: 100%; min-height: 246px; background-color: #595959" frameborder="0"></iframe>';
// The legacy preview rendered the YouTube layout with this fixed background color.
const YOUTUBE_PREVIEW_BACKGROUND = "#F2CFBA";

export const useYoutubeData = (props: QRProps<"youtube">): UseYoutubeDataResult => {
  const { data, isInitPreview, context } = usePreviewData(props);
  const { toast } = useToast();
  const { t: tValidations } = useTranslation(NAMESPACES.VALIDATIONS);

  const [youtubeEmbedLink, setYoutubeEmbedLink] = useState("");

  useEffect(() => {
    if (isInitPreview) {
      return;
    }

    if (!data?.youtubeUrl) {
      // No URL yet: block the editor's Next button until one is provided.
      context?.setValidationError?.(true);
      return;
    }

    axios
      .get(`${YOUTUBE_OEMBED_ENDPOINT}${encodeURIComponent(data.youtubeUrl)}`)
      .then((res) => {
        setYoutubeEmbedLink(res.data.html);
        context?.setValidationError?.(false);
      })
      .catch(() => {
        // ValidationError prevents the Next button from moving to the next step.
        context?.setValidationError?.(true);
        logError(new Error("Error fetching YouTube embed link"), {
          youtubeUrl: data?.youtubeUrl,
        });
        setYoutubeEmbedLink(EMPTY_VIDEO_IFRAME);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.youtubeUrl, isInitPreview]);

  const handleVideoReady = (isLoaded?: boolean) => {
    if (!isLoaded) {
      setYoutubeEmbedLink("");
    }
  };

  const handleVideoError = () => {
    const toastContent = isFileAblob(youtubeEmbedLink)
      ? InvalidVideoFormatToast(tValidations)
      : InvalidYTProviderToast(tValidations);

    toast({
      type: "custom",
      content: () => toastContent,
      marginBottom: 80,
      duration: 10000,
    });
  };

  return {
    data,
    isInitPreview,
    logoUrl: data?.logoUrl,
    youtubeEmbedLink,
    backgroundColor: YOUTUBE_PREVIEW_BACKGROUND,
    handleVideoReady,
    handleVideoError,
  };
};
